import { useEffect, useState } from 'preact/hooks'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  getAvailabilityMap,
  getMe,
  resetDemoTenant,
  setMyName,
  setMyPhone,
  setMySchedule,
  setSiteAvailability,
} from '../api/endpoints'
import { Banner, Button, Card, Switch } from '../components/ui'
import { clearSession, getStoredOperator, isDemoViewer } from '../auth/session'
import { useLocation } from 'wouter-preact'
import { siteMeta } from '../utils/site'
import { isDemoMode, setDemoMode } from '../utils/demo-mode'
import { getThemePref, setThemePref, type ThemePref } from '../utils/theme'
import type { AlertSchedule, SiteId } from '../types'
import {
  currentPushPermission,
  isIos,
  isStandalonePwa,
  isSubscribed,
  subscribeToPush,
} from '../push/register'

export function SettingsPage() {
  const [, setLocation] = useLocation()
  const queryClient = useQueryClient()
  const stored = getStoredOperator()
  const [themeChoice, setThemeChoice] = useState<ThemePref>(getThemePref())
  const [alertPhone, setAlertPhone] = useState<string>('')
  const phoneSave = useMutation({
    mutationFn: (phone: string) => setMyPhone(phone),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['me'] }),
  })

  const [displayName, setDisplayName] = useState<string>('')
  const [nameLoaded, setNameLoaded] = useState(false)
  const nameSave = useMutation({
    mutationFn: (name: string) => setMyName(name),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['me'] }),
  })

  // Alert schedule (quiet hours) — local draft state, saved explicitly.
  const DEFAULT_SCHEDULE: AlertSchedule = {
    enabled: false,
    days: ['mon', 'tue', 'wed', 'thu', 'fri'],
    start: '09:00',
    end: '17:30',
  }
  const [schedule, setSchedule] = useState<AlertSchedule>(DEFAULT_SCHEDULE)
  const [scheduleLoaded, setScheduleLoaded] = useState(false)
  const scheduleSave = useMutation({
    mutationFn: (s: AlertSchedule) => setMySchedule(s.enabled ? s : null),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['me'] }),
  })

  const meQuery = useQuery({
    queryKey: ['me'],
    queryFn: getMe,
  })
  const demoViewer = isDemoViewer()
  const demoReset = useMutation({
    mutationFn: resetDemoTenant,
    onSuccess: () => queryClient.invalidateQueries(),
  })
  const availabilityQuery = useQuery({
    queryKey: ['availability'],
    queryFn: getAvailabilityMap,
  })

  const toggle = useMutation({
    mutationFn: ({ site, available }: { site: SiteId; available: boolean }) =>
      setSiteAvailability(site, available),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['availability'] })
    },
  })

  useEffect(() => {
    if (meQuery.data?.operator && !nameLoaded) {
      setNameLoaded(true)
      setDisplayName(meQuery.data.operator.name ?? '')
    }
    if (meQuery.data?.operator && !scheduleLoaded) {
      setScheduleLoaded(true)
      if (meQuery.data.operator.alertSchedule) {
        setSchedule(meQuery.data.operator.alertSchedule)
      }
    }
    if (meQuery.data?.operator && alertPhone === '') {
      setAlertPhone(meQuery.data.operator.phone ?? '')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meQuery.data?.operator?.phone])

  const sites = meQuery.data?.sites ?? stored?.sites ?? []
  const availability = availabilityQuery.data?.availability ?? {}

  // Push state
  const [pushPerm, setPushPerm] = useState<NotificationPermission>('default')
  const [pushSubscribed, setPushSubscribed] = useState<boolean>(false)
  const [pushBusy, setPushBusy] = useState(false)
  const [pushError, setPushError] = useState<string | null>(null)

  useEffect(() => {
    ;(async () => {
      setPushPerm(await currentPushPermission())
      setPushSubscribed(await isSubscribed())
    })()
  }, [])

  async function handleEnablePush() {
    setPushBusy(true)
    setPushError(null)
    try {
      const ok = await subscribeToPush()
      setPushPerm(await currentPushPermission())
      setPushSubscribed(await isSubscribed())
      if (!ok && Notification.permission !== 'granted') {
        setPushError(
          'Notifications are blocked. Open Safari / browser settings to enable them for this site.'
        )
      }
    } catch (err) {
      setPushError(
        err instanceof Error ? err.message : 'Couldn’t enable notifications.'
      )
    } finally {
      setPushBusy(false)
    }
  }

  const showIosInstallHint = isIos() && !isStandalonePwa()

  return (
    <>
      <h1 style={{ marginBottom: 4 }}>Settings</h1>
      {stored?.email && (
        <p style={{ color: 'var(--m-text-muted)', marginBottom: 20 }}>
          Signed in as <strong>{stored.email}</strong>
        </p>
      )}

      {showIosInstallHint && (
        <Banner variant="info">
          <strong>To get notifications on iPhone:</strong> tap the Share icon
          in Safari → <em>Add to Home Screen</em>, then open Monty
          from its icon. iOS only allows push after the app is installed and
          launched from the home screen at least once.
        </Banner>
      )}

      {demoViewer && (
        <Card>
          <h3 style={{ marginBottom: 4 }}>You're in the demo</h3>
          <p style={{ fontSize: 13, color: 'var(--m-text-muted, #6b7280)', marginBottom: 12 }}>
            This account only sees Spoke &amp; Sprocket Cycles, a fictional shop. In a
            live account this page is where you set up push notifications, alert
            numbers and quiet hours.
          </p>
          <Button
            variant="outline"
            onClick={() => {
              clearSession()
              setLocation('/login')
            }}
          >
            Exit demo
          </Button>
        </Card>
      )}

      {!demoViewer && (<>
      {/* Notifications */}
      <Card class="setting-group">
        <h3 style={{ marginBottom: 12 }}>Notifications</h3>

        {pushSubscribed ? (
          <Banner variant="success">
            Notifications are enabled on this device.
          </Banner>
        ) : pushPerm === 'denied' ? (
          <Banner variant="warn">
            Notifications are blocked in your browser settings. Unblock them to
            receive new-enquiry alerts.
          </Banner>
        ) : (
          <>
            <p
              style={{
                color: 'var(--m-text-muted)',
                marginBottom: 12,
                fontSize: 'var(--m-text-sm)',
              }}
            >
              Get a push alert the moment a new enquiry comes in, even when the
              app is closed.
            </p>
            <Button
              variant="primary"
              loading={pushBusy}
              onClick={handleEnablePush}
            >
              Enable notifications
            </Button>
          </>
        )}
        {pushError && (
          <div style={{ marginTop: 12 }}>
            <Banner variant="error">{pushError}</Banner>
          </div>
        )}
      </Card>

      {/* Availability */}
      <div style={{ height: 20 }} />
      <Card>
        <h3 style={{ marginBottom: 4 }}>Availability</h3>
        <p
          style={{
            color: 'var(--m-text-muted)',
            fontSize: 'var(--m-text-sm)',
            marginBottom: 12,
          }}
        >
          Off means the chat widget tells visitors you’re away. Turning a site
          back on resumes the normal schedule.
        </p>
        {sites.length === 0 && (
          <p style={{ color: 'var(--m-text-muted)' }}>
            No sites assigned to your account.
          </p>
        )}
        {sites.map((site) => {
          const state = availability[site]
          const isOnline = state?.status === 'online'
          const meta = siteMeta(site)
          return (
            <div class="setting-row" key={site}>
              <div>
                <div class="setting-label">{meta.name}</div>
                <div class="setting-sub">
                  {state
                    ? isOnline
                      ? 'Currently taking enquiries'
                      : state.nextOpenTime
                        ? `Offline · back at ${state.nextOpenTime}`
                        : 'Offline'
                    : '—'}
                </div>
              </div>
              <Switch
                checked={isOnline}
                onChange={(v) =>
                  toggle.mutate({ site, available: v })
                }
                disabled={toggle.isPending}
                label={`Toggle availability for ${meta.name}`}
              />
            </div>
          )
        })}
      </Card>

      {/* My profile — display name */}
      <div style={{ height: 20 }} />
      <Card>
        <h3 style={{ marginBottom: 4 }}>Display name</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 220, fontSize: 13, color: 'var(--m-text-muted, #6b7280)' }}>
            Shown on your replies in conversation threads and on the
            "handling this" label colleagues see. Empty = your email address.
          </div>
          <input
            type="text"
            value={displayName}
            onInput={(e) => setDisplayName((e.target as HTMLInputElement).value)}
            placeholder="e.g. Martin"
            maxLength={60}
            style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid var(--m-border, #d1d5db)', fontSize: 14, width: 170, background: 'var(--m-surface, #fff)', color: 'var(--m-text, #111827)' }}
          />
          <Button
            variant="outline"
            onClick={() => nameSave.mutate(displayName)}
            loading={nameSave.isPending}
            disabled={displayName.trim() === (meQuery.data?.operator?.name ?? '')}
          >
            Save
          </Button>
        </div>
        {nameSave.isError && (
          <Banner variant="error" style={{ marginTop: 10 }}>
            {(nameSave.error as Error)?.message ?? "Couldn't save the name."}
          </Banner>
        )}
        {nameSave.isSuccess && (
          <div style={{ marginTop: 8, fontSize: 12, color: 'var(--m-success, #15803d)' }}>
            Saved — replies will show as {nameSave.data?.name ?? meQuery.data?.operator?.email}.
          </div>
        )}
      </Card>

      {/* Escalation alerts — my number */}
      <div style={{ height: 20 }} />
      <Card>
        <h3 style={{ marginBottom: 4 }}>Escalation alerts</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 220, fontSize: 13, color: 'var(--m-text-muted, #6b7280)' }}>
            Your mobile for SMS, voice-call, and WhatsApp escalation alerts on
            your sites. Leave empty for push notifications only.
          </div>
          <input
            type="tel"
            value={alertPhone}
            onInput={(e) => setAlertPhone((e.target as HTMLInputElement).value)}
            placeholder="+44 7… or 07…"
            style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid var(--m-border, #d1d5db)', fontSize: 14, width: 170, background: 'var(--m-surface, #fff)', color: 'var(--m-text, #111827)' }}
          />
          <Button
            variant="outline"
            onClick={() => phoneSave.mutate(alertPhone)}
            loading={phoneSave.isPending}
            disabled={alertPhone === (meQuery.data?.operator?.phone ?? '')}
          >
            Save
          </Button>
        </div>
        {phoneSave.isError && (
          <Banner variant="error" style={{ marginTop: 10 }}>
            {(phoneSave.error as Error)?.message ?? "Couldn't save the number."}
          </Banner>
        )}
        {phoneSave.isSuccess && (
          <div style={{ marginTop: 8, fontSize: 12, color: 'var(--m-success, #15803d)' }}>
            Saved — alerts will go to {phoneSave.data?.phone ?? 'push only'}.
          </div>
        )}
      </Card>

      {/* Alert schedule — quiet hours */}
      <div style={{ height: 20 }} />
      <Card>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0 }}>Alert schedule</h3>
          <Switch
            checked={schedule.enabled}
            onChange={(v: boolean) => setSchedule({ ...schedule, enabled: v })}
            label="Only alert me during set hours"
          />
        </div>
        <div style={{ marginTop: 6, fontSize: 13, color: 'var(--m-text-muted, #6b7280)' }}>
          {schedule.enabled
            ? 'Outside these hours you get no push, WhatsApp, SMS or call alerts. Conversations still land in the inbox, and visitors get the usual email follow-up if nobody picks up.'
            : 'Off — you receive alerts at any time.'}
        </div>
        {schedule.enabled && (
          <>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 12 }}>
              {(['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const).map((d) => {
                const on = schedule.days.includes(d)
                return (
                  <button
                    key={d}
                    onClick={() =>
                      setSchedule({
                        ...schedule,
                        days: on ? schedule.days.filter((x) => x !== d) : [...schedule.days, d],
                      })
                    }
                    style={{
                      padding: '6px 12px',
                      borderRadius: 999,
                      fontSize: 13,
                      cursor: 'pointer',
                      border: '1px solid var(--m-border, #d1d5db)',
                      background: on ? 'var(--m-primary, #4f46e5)' : 'var(--m-surface, #fff)',
                      color: on ? '#fff' : 'var(--m-text, #111827)',
                      textTransform: 'capitalize',
                    }}
                  >
                    {d}
                  </button>
                )
              })}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, flexWrap: 'wrap', fontSize: 14 }}>
              <span>From</span>
              <input
                type="time"
                value={schedule.start}
                onInput={(e) => setSchedule({ ...schedule, start: (e.target as HTMLInputElement).value })}
                style={{ padding: '6px 8px', borderRadius: 8, border: '1px solid var(--m-border, #d1d5db)', fontSize: 14, background: 'var(--m-surface, #fff)', color: 'var(--m-text, #111827)' }}
              />
              <span>to</span>
              <input
                type="time"
                value={schedule.end}
                onInput={(e) => setSchedule({ ...schedule, end: (e.target as HTMLInputElement).value })}
                style={{ padding: '6px 8px', borderRadius: 8, border: '1px solid var(--m-border, #d1d5db)', fontSize: 14, background: 'var(--m-surface, #fff)', color: 'var(--m-text, #111827)' }}
              />
              {schedule.start > schedule.end && (
                <span style={{ fontSize: 12, color: 'var(--m-text-muted, #6b7280)' }}>
                  (overnight — runs past midnight)
                </span>
              )}
            </div>
          </>
        )}
        <div style={{ marginTop: 12 }}>
          <Button
            variant="outline"
            onClick={() => scheduleSave.mutate(schedule)}
            loading={scheduleSave.isPending}
            disabled={schedule.enabled && schedule.days.length === 0}
          >
            Save schedule
          </Button>
        </div>
        {scheduleSave.isError && (
          <Banner variant="error" style={{ marginTop: 10 }}>
            {(scheduleSave.error as Error)?.message ?? "Couldn't save the schedule."}
          </Banner>
        )}
        {scheduleSave.isSuccess && (
          <div style={{ marginTop: 8, fontSize: 12, color: 'var(--m-success, #15803d)' }}>
            Saved{schedule.enabled ? ` — alerts ${schedule.days.length} day${schedule.days.length === 1 ? '' : 's'} a week, ${schedule.start}–${schedule.end}.` : ' — alerts at any time.'}
          </div>
        )}
      </Card>
      </>)}

      {/* Appearance — per-device */}
      <div style={{ height: 20 }} />
      <Card>
        <h3 style={{ marginBottom: 4 }}>Appearance</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 200, fontSize: 13, color: 'var(--m-text-muted, #6b7280)' }}>
            Light or dark, or follow this device's system setting.
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            {(['system', 'light', 'dark'] as const).map((opt) => (
              <button
                key={opt}
                type="button"
                onClick={() => {
                  setThemePref(opt)
                  setThemeChoice(opt)
                }}
                style={{
                  padding: '6px 14px',
                  borderRadius: 999,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  border: '1px solid var(--m-border, #d1d5db)',
                  background: themeChoice === opt ? 'var(--m-primary, #1e3a8a)' : 'transparent',
                  color: themeChoice === opt ? '#fff' : 'var(--m-text, #111827)',
                }}
              >
                {opt === 'system' ? 'System' : opt === 'light' ? 'Light' : 'Dark'}
              </button>
            ))}
          </div>
        </div>
      </Card>

      {/* Demo mode — admin only, this device only */}
      {meQuery.data?.operator?.isAdmin === true && (
        <>
          <div style={{ height: 20 }} />
          <Card>
            <h3 style={{ marginBottom: 4 }}>Demo mode</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <div style={{ flex: 1, fontSize: 13, color: 'var(--m-text-muted, #6b7280)' }}>
                Hides all real client sites on this device — only the demo tenant
                (Spoke &amp; Sprocket) shows anywhere in the app. For screen recording.
                Nothing changes server-side or for other operators.
              </div>
              <Switch
                checked={isDemoMode()}
                onChange={(v) => setDemoMode(v)}
                label="Toggle demo mode"
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 16, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 220, fontSize: 13, color: 'var(--m-text-muted, #6b7280)' }}>
                Reset the demo shop to its showcase week: clears every chat on the
                demo site, including anything public visitors typed, and makes the
                seeded conversations recent again. Also runs every night.
              </div>
              <Button
                variant="outline"
                loading={demoReset.isPending}
                onClick={() => demoReset.mutate()}
              >
                Reset demo
              </Button>
            </div>
            {demoReset.isSuccess && (
              <div style={{ marginTop: 8, fontSize: 12, color: 'var(--m-success, #15803d)' }}>
                Demo reset: {demoReset.data.conversations} showcase conversations.
              </div>
            )}
            {demoReset.isError && (
              <Banner variant="error" style={{ marginTop: 10 }}>
                {(demoReset.error as Error)?.message ?? "Couldn't reset the demo."}
              </Banner>
            )}
          </Card>
        </>
      )}

      {/* Account */}
      <div style={{ height: 20 }} />
      <Card>
        <h3 style={{ marginBottom: 12 }}>Account</h3>
        <Button
          variant="outline"
          onClick={() => {
            clearSession()
            setLocation('/login')
          }}
        >
          Sign out
        </Button>
      </Card>

      {/* Build info — inlined at build time via vite.config.ts define block.
          Lets an operator confirm they're on the latest deploy without
          having to open devtools. */}
      <div
        style={{
          marginTop: 24,
          padding: '0 4px',
          fontSize: 12,
          color: 'var(--m-text-muted, #6b7280)',
          textAlign: 'center',
          fontVariantNumeric: 'tabular-nums',
        }}
        title={`Build ${__APP_VERSION__} · ${__BUILD_DATE__}`}
      >
        v{__APP_VERSION__} · built {formatBuildDate(__BUILD_DATE__)}
      </div>
    </>
  )
}

/** Render the ISO build timestamp as "YYYY-MM-DD HH:mm" in the viewer's
 *  local time. Full ISO stays on the hover title for anyone who cares. */
function formatBuildDate(iso: string): string {
  try {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return iso
    const pad = (n: number) => String(n).padStart(2, '0')
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
  } catch {
    return iso
  }
}
